let ENV = 'prod'; // dev | test | prod | ni | ni2

const urlConfig = {
  dev: 'https://ceapi.hanlandglobal.wang', // yourslink的项目测试环境
  test: 'https://ceapi.hanlandglobal.wang', // yourslink的项目测试环境
  prod: 'https://api.yourslink.co.uk', // 线上

};

/** 根据 ENV 解析出的当前 API 基地址 */
let baseUrl = urlConfig[ENV];

/**
 * 将键值对对象转换为 { name, value } 选项列表
 * @param {Object} [origin={}] - 原始键值对，key 为 value，value 为 name
 * @returns {{ name: string, value: string }[]}
 */
function transformObjectToOptionList (origin = {}) {
  const list = [];
  for (const key in origin) {
    list.push({ name: origin[key], value: key });
  }
  return list;
}

/**
 * 为图片 URL 添加七牛云 CDN 压缩参数（imageView2 等比缩放）。
 *
 * 默认参数为 `imageView2/2/w/400`，即单纯等比缩放（宽高），宽度 400px。
 * 已包含 `imageView` 参数的 URL 不会重复添加，直接原样返回。
 *
 * @param {string} url - 原始图片 URL
 * @param {number} [width=400] - 缩放宽度（px）
 * @param {Object} [options] - 压缩选项
 * @param {string?} [options.format] - 输出图片格式，传入空值则不添加 format 参数
 * @param {number?} [options.quality] - 压缩质量（1-100），传入空值则不添加 q 参数
 * @returns {string} 带压缩参数的 URL；当 url 为空或已含 imageView 参数时原样返回
 */
function getCompressedImageUrl (url, width = 400, options = { format: null, quality: null }) {
  if (!url || url.includes('imageView')) return url;
  const sep = url.includes('?') ? '&' : '?';
  const formatStr = options.format ? `/format/${options.format}` : '';
  const quailtyStr = options.quality ? `/q/${options.quality}` : '';
  const widthStr = width ? `/w/${width}` : '';
  return `${url}${sep}imageView2/2${widthStr}${formatStr}${quailtyStr}`;
}

/**
 * 批量将图片 URL 列表转换为 { origin, thumb, large } 结构
 * origin 用于全屏查看（原图），thumb 用于缩略图（默认 400px），large 用于轮播大图（1200px）
 * @param {string[]} [imageList=[]] - 原始图片 URL 列表
 * @param {number} [width=400] - 缩略图宽度
 * @returns {{ origin: string, thumb: string, large: string }[]}
 */
function transformImagesToCompressObj (imageList = [], width) {
  return imageList.map(x => ({
    origin: x,
    thumb: getCompressedImageUrl(x, width || 400),
    large: getCompressedImageUrl(x, 1200),
  }))
}
/**
 * 批量将图片 URL 转换为 { origin, webp, avif } 结构
 * @param {string} url - 原始图片 URL
 * @param {number} [width=400] - 缩略图宽度
 * @returns {{ origin: string, webp: string, avif: string }}
 */
function transformImageTypeObj (url, width) {
  return {
    origin: getCompressedImageUrl(url, width || 400),
    webp: getCompressedImageUrl(url, width || 400, { format: 'webp', quality: 90 }),
    avif: getCompressedImageUrl(url, width || 400, { format: 'avif', quality: 90 }),
  };
}

/**
 * 将 HTML 富文本转换为纯文本
 * 浏览器端使用 DOM 解析，Node 端使用正则回退
 * @param {string} [text=''] - 可能包含 HTML 标签的文本
 * @returns {string} 纯文本
 */
function getPureText (text = '') {
  let str = text.trim();
  if (str.includes('<')) {
    if (typeof document !== 'undefined') {
      const el = document.createElement('div');
      el.innerHTML = str;
      const pureText = el.textContent || el.innerText || '';
      return pureText.trim();
    }
    return str
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  } else {
    return str;
  }
}


const FILE_TYPE_MAP = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif', 'avif'],
  music: ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'ape', 'aiff', 'aif', 'opus'],
  video: ['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm', 'm4v', 'mpg', 'mpeg', '3gp', 'ts', 'rmvb'],
  pdf: ['pdf'],
  word: ['doc', 'docx'],
  excel: ['xls', 'xlsx', 'csv'],
  ppt: ['ppt', 'pptx'],
  txt: ['txt'],
  zip: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'],
};

/**
 * 根据文件名或 URL 快速解析文件类型
 * @param {string} filename - 文件名或 URL
 * @returns {string} 文件类型，如 'image'、'pdf'、'word' 等，无法识别时返回 'other'
 */
function getFileType (filename) {
  if (!filename) return 'other';
  const ext = (filename.split('.').pop() || '').split('?')[0].split('#')[0].toLowerCase();
  for (const [type, exts] of Object.entries(FILE_TYPE_MAP)) {
    if (exts.includes(ext)) return type;
  }
  return 'other';
}

function isValidCoord (v) {
  // 空值一律拒绝
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean' || typeof v === 'object') return false;
  let val;
  if (typeof v === 'number') {
    val = v;
  } else if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') return false;               // 空字符串 / 纯空白
    if (t.toLowerCase() === 'nan') return false; // 字符串 "NaN"
    val = Number(t);
    if (!t.match(/^[+-]?\d+(\.\d+)?$/)) return false; // "南纬23.5°" → 拒
  } else {
    return false;                             // symbol / bigint 等
  }
  return Number.isFinite(val);                // NaN / ±Infinity 拒
}
function isValidLatLng (location) {
  if (!Array.isArray(location) || location.length !== 2) return false;
  const [lngInput, latInput] = location;
  if (!isValidCoord(latInput) || !isValidCoord(lngInput)) return false;
  let ln = Number(lngInput);
  let la = Number(latInput);

  if (Math.abs(la) > 90 || Math.abs(ln) > 180) return false;
  if (la === 0 && ln === 0) return false;

  // 智能纠偏：检测经纬度颠倒（英国房产）
  // 英国纬度范围约 49.5 ~ 61.0，经度范围约 -11.0 ~ 2.5
  if (ln >= 49.0 && ln <= 61.5 && la >= -11.5 && la <= 3.5) {
    const temp = ln;
    ln = la;
    la = temp;
  }

  // 严格校验是否在英国有效地理区域内（留合理余量 [48, 62] 和 [-12, 4]）
  if (la < 48.0 || la > 62.0 || ln < -12.0 || ln > 4.0) {
    return false;
  }

  return [la, ln];
}

/**
 * 纠偏并返回英国房产有效 [lng, lat]；若完全无效则返回 null
 */
function normalizeUkCoordinates (lngInput, latInput) {
  const res = isValidLatLng([lngInput, latInput]);
  if (!res) return null;
  // res 为 [lat, lng]，返回 [lng, lat]
  return [res[1], res[0]];
}


module.exports = {
  ENV,
  baseUrl,
  transformObjectToOptionList,
  getCompressedImageUrl,
  transformImagesToCompressObj,
  transformImageTypeObj,
  getPureText,
  getFileType,
  isValidLatLng,
  normalizeUkCoordinates
};
